<template>
  <v-dialog :value="value" max-width="670" @click:outside="cancel" @input.self="$emit('input', $event)">
    <mf-loading loader-type="dialog" :boolean-loader="loadingDialog">
      <v-card>
        <v-card-title class="pa-6 pb-5">
          <h1 class="pa-0 section-title mb-2">{{ title }}</h1>
          <span class="user-form-description">
            Esse modelo vai ser guardado e vai poder ser utilizado novamente por você ou qualquer outro usuário da sua plataforma.
          </span>
        </v-card-title>
        <v-card-text class="pt-1">
          <div class="d-flex align-center" style="gap: 24px">
            <v-text-field v-model="user.name" label="Nome" autofocus dense outlined :error-messages="nameErrorMessages" />
            <v-text-field
              v-model="user.phone"
              v-mask="['+55 (##) #####-####', '+55 (##) ####-####']"
              :error-messages="phoneErrorMessages"
              dense
              outlined
              label="Telefone"
            />
          </div>

          <div class="d-flex">
            <v-text-field
              v-if="!showNewEmailInput"
              v-model="user.email"
              label="E-mail"
              autocomplete="off"
              :error-messages="emailErrorMessages"
              :disabled="!isCreateUser"
              outlined
              dense
            />
            <v-text-field v-if="showNewEmailInput" v-model="user.newEmail" label="Novo e-mail" :error-messages="newEmailErrorMessages" outlined dense />

            <mf-button
              v-if="!isCreateUser"
              text
              color="marca-0-l40"
              class="mt-1 ml-2"
              icon="autorenew"
              label="Trocar email"
              @click="showNewEmailInput = !showNewEmailInput"
            />
          </div>

          <v-text-field
            v-model="user.password"
            label="Senha"
            autocomplete="off"
            hint="Utilize pelo menos 10 caracteres, sendo uma letra maiúscula, uma letra minúscula e um número"
            :error-messages="passwordErrorMessages"
            :append-icon="visibility ? 'visibility' : 'visibility_off'"
            :type="visibility ? 'password' : 'text'"
            outlined
            dense
            @click:append="() => (visibility = !visibility)"
          />
          <v-text-field
            v-model="user.passwordCheck"
            label="Confirmação da senha"
            autocomplete="off"
            hint="Repita a senha anterior"
            :append-icon="visibility ? 'visibility' : 'visibility_off'"
            :type="visibility ? 'password' : 'text'"
            :error-messages="passwordCheckErrorMessages"
            outlined
            dense
            @click:append="() => (visibility = !visibility)"
          />

          <label class="field-label" for="admin">Tipo de usuário</label>
          <div class="d-flex align-center mt-2 mb-6 pl-2">
            <div class="d-flex align-center">
              <mf-toggle v-model="user.admin" hide-details label="Admin" />
              <mf-tooltip top max-width="350px">
                <template v-slot:activator="{ on }">
                  <span v-on="on"><mf-icon class="ml-2" size="16">info</mf-icon></span>
                </template>
                <span>Um usuário admin tem acesso à página de permissionamento e a edição e criação de novos usuários na sua plataforma.</span>
              </mf-tooltip>
            </div>

            <mf-toggle v-model="user.active" hide-details class="ml-8" :label="user.active ? 'Usuário ativo' : 'Usuário inativo'" />
          </div>
          <label class="field-label pt-3" for="profiles">Selecione os perfis em que esse usuário se encaixa</label>
          <access-profile
            ref="access-profile"
            :is-create-user="isCreateUser"
            :profiles.sync="new_profiles"
            :profile-error="false"
            :user.sync="user"
            :user-profile-error="validateProfiles"
            :user-ecommerce-error="validateEcommerce"
            :ecommerce.sync="new_ecommerce"
            :ecommerce-error="false"
            :colLength="6"
          />
          <mf-action-buttons
            :primary-button="{
              text: 'Salvar',
              action: isCreateUser ? submitCreate : submitEdit,
              isVisible: true,
              isLoading: loading,
            }"
            :extra-button="{
              text: 'Cancelar',
              action: cancel,
              isVisible: true,
            }"
          />
        </v-card-text>
      </v-card>
    </mf-loading>
  </v-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { required, sameAs, minLength, maxLength } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { EMAIL_DOMAIN } from '@/config/constants.js'
import { profilesPages } from '../../mixins/user-profiles'
import { strongPassword } from '@/helpers/validators'
import { MUTATION_UPDATE_USER, MUTATION_CREATE_USER, QUERY_GET_USER } from '@/modules/usuarios/graphql'

const ROLE_ADMIN = 'mercafacilAdmin'

export default {
  name: 'UserForm',
  components: {
    AccessProfile: () => import('./AccessProfile.vue'),
  },
  mixins: [profilesPages],
  props: {
    email: {
      type: String,
      default: () => '',
    },
    phoneRequired: {
      type: Boolean,
      default: () => false,
    },
    value: Boolean,
  },
  data: () => ({
    showNewEmailInput: false,
    loadingDialog: false,
    loading: false,
    visibility: true,
    user: {
      name: null,
      email: null,
      password: null,
      passwordCheck: null,
      phone: null,
      active: true,
      admin: false,
      profiles: [],
      ecommerce: [],
      pages: [],
    },
    new_profiles: [],
    new_ecommerce: [],
    ecommercePermissions: [],
    pages: [],
    stores: [],
  }),
  computed: {
    isCreateUser() {
      return !this.email
    },
    title() {
      return this.isCreateUser ? 'Novo usuário' : 'Editar usuário'
    },
    nameErrorMessages() {
      if (this.$v.user.name.$error) return 'Campo obrigatório'
      return ''
    },
    phoneErrorMessages() {
      if (this.$v.user?.phone?.$error) return 'Insira um número de telefone válido'
      return ''
    },
    emailErrorMessages() {
      if (this.$v.user.email.$error) return 'Campo obrigatório'
      return ''
    },
    newEmailErrorMessages() {
      if (this.$v.user.newEmail && this.$v.user.newEmail.$error) {
        if (this.$v.user.newEmail.$error) return 'Campo obrigatório'
      }

      return ''
    },
    passwordErrorMessages() {
      if (this.$v.user.password && this.$v.user.password.$error) {
        if (!this.$v.user.password.required) {
          return 'Campo obrigatório.'
        }
        if (!this.$v.user.password.minLength) {
          return 'Utilize pelo menos 10 caracteres'
        }
        if (!this.$v.user.password.maxLength) {
          return 'Utilize no máximo 24 caracteres'
        }
        if (!this.$v.user.password.strongPassword) {
          return 'Utilize pelo menos uma letra maiúscula, uma letra minúscula e um número'
        }
      }
      return ''
    },
    passwordCheckErrorMessages() {
      if (this.$v.user.password && this.$v.user.passwordCheck.$error) {
        if (!this.$v.user.passwordCheck.samePassword) {
          return 'Repita a senha anterior'
        }
        if (!this.$v.user.passwordCheck.required) {
          return 'Campo obrigatório.'
        }
        if (!this.$v.user.passwordCheck.minLength) {
          return 'Utilize pelo menos 10 caracteres'
        }
        if (!this.$v.user.passwordCheck.maxLength) {
          return 'Utilize no máximo 24 caracteres'
        }
        if (!this.$v.user.passwordCheck.strongPassword) {
          return 'Utilize pelo menos uma letra maiúscula, uma letra minúscula e um número'
        }
      }
      return ''
    },
    validateProfiles() {
      return this.$v.user.profiles && this.$v.user.profiles.$error
    },
    validateEcommerce() {
      return this.$v.user.ecommerce && this.$v.user.ecommerce.$error
    },
  },
  watch: {
    value(value) {
      if (value && this.email) {
        this.$v.$reset()
        this.getUser()
        return
      }

      if (this.$refs['access-profile']) {
        this.$refs['access-profile'].clearFields()
      }
    },
  },
  methods: {
    async getUser() {
      this.loadingDialog = true
      try {
        const {
          data: { getPlatformUser },
        } = await this.$apollo.query({
          query: QUERY_GET_USER,
          fetchPolicy: 'no-cache',
          variables: {
            email: this.email,
          },
        })

        const {
          name,
          email,
          active,
          phone,
          permissions: { crm, ecommerce, stores },
        } = getPlatformUser

        this.user = {
          name,
          email,
          active,
          phone: phone || '',
          profiles: this.generateEditProfiles(crm?.roles),
          accessSensitiveData: crm.accessSensitiveData,
          accessSensitiveDataEmployees: crm.accessSensitiveDataEmployees,
          downloadSensitiveDataPermission: crm.downloadSensitiveDataPermission,
          ecommerce: ecommerce?.roles ?? [],
          permissions: getPlatformUser.permissions,
        }
        this.stores = stores.map(({ id, accountId, club, ecommerce }) => ({ id, accountId, club, ecommerce }))
        this.new_ecommerce = ecommerce?.roles
        this.ecommercePermissions = ecommerce?.permissions
        this.pages = crm?.permissions

        if (crm?.roles.find((role) => role === 'admin')) {
          this.user.admin = true
        }
        if (crm?.roles.find((role) => role === 'mercafacilAdmin')) {
          this.user.crmAdmin = true
        }
        if (ecommerce?.roles.find((role) => role === 'mercafacilAdmin')) {
          this.user.ecommerceAdmin = true
        }
      } catch (error) {
        this.$snackbar({ message: 'Não foi possível alterar este usuário' })
      } finally {
        this.loadingDialog = false
      }
    },
    cancel() {
      this.user = {
        name: null,
        email: null,
        admin: false,
        active: true,
        ecommerce: [],
        profiles: [],
      }
      this.new_ecommerce = []
      this.new_profiles = []
      this.$v.$reset()
      this.$emit('input', false)
    },
    generateProfiles(profilesPages) {
      let arrayOfProfiles = []
      profilesPages.forEach(function (item) {
        arrayOfProfiles.push(item.value)
      })
      return arrayOfProfiles
    },
    generateEditProfiles(profilesPages) {
      if (profilesPages) {
        let profiles = []
        profilesPages.map((obj) => {
          this.profilesPages.filter((item) => item.value === obj).forEach((i) => profiles.push(i))
        })
        return profiles
      }
    },
    formatsPayloadToCreate() {
      let usernameWithDomain = cloneDeep(this.user.email)

      if (!this.user.email.includes('@')) {
        usernameWithDomain = usernameWithDomain + EMAIL_DOMAIN
      }

      const payload = {
        name: this.user.name,
        phone: this.user?.phone?.trim(),
        email: usernameWithDomain.trim(),
        password: this.user.password,
        active: this.user.active,
        permissionValue: {
          stores: [],
          crm: {
            permissions: [],
            roles: [...this.generateProfiles(this.user.profiles)],
            accessSensitiveData: false,
            accessSensitiveDataEmployees: this.user.admin ?? false,
            downloadSensitiveDataPermission: false,
          },
          ecommerce: {
            permissions: [],
            roles: [...this.user.ecommerce],
          },
        },
      }

      payload.permissionValue.crm.permissions = this.generatePermissionsFromRoles(payload.permissionValue.crm.roles)
      payload.permissionValue.crm.accessSensitiveData = this.user.admin || this.roleHasPiiAccess(payload.permissionValue.crm.roles)
      payload.permissionValue.crm.downloadSensitiveDataPermission = false
      if (this.$getLoggedUser().has_mercapromo) {
        const onlyMercapromoPages = ['industria/ofertas', 'industria/ofertas_trade', 'industria/sms_trade']

        payload.permissionValue.crm.permissions.push(...onlyMercapromoPages)
      }

      if (this.user.admin) {
        const adminUserPages = ['usuarios/editar', 'usuarios/permissionamento', 'configuracoes/permissionamento-filiado']

        payload.permissionValue.crm.roles.push('admin')
        payload.permissionValue.crm.permissions.push(...adminUserPages)
      }

      payload.permissionValue.ecommerce.roles
        .map((el) => this.ecommerceProfiles.find((ej) => ej.value === el)?.permissions)
        .filter((el) => el)
        .forEach((el) => {
          payload.permissionValue.ecommerce.permissions.push(...el)
          payload.permissionValue.ecommerce.permissions = payload.permissionValue.ecommerce.permissions.filter(
            (value, index, array) => array.indexOf(value) === index
          )
        })

      return payload
    },
    formatsPayloadToEdit() {
      let usernameWithDomain = cloneDeep(this.user.email)

      if (!this.user.email.includes('@')) {
        usernameWithDomain = usernameWithDomain + EMAIL_DOMAIN
      }

      let alteredFields = {}

      if (this.user.profiles) {
        alteredFields = {
          id: this.id,
          name: this.user.name.trim(),
          phone: this.user?.phone?.trim(),
          login: usernameWithDomain.trim(),
          active: this.user.active,
          admin: this.user.admin,
          pages: this.adminUserPages(),
          profiles: this.generateProfiles(this.user.profiles),
          ecommerce: this.user.ecommerce,
        }
      } else {
        alteredFields = {
          id: this.id,
          name: this.user.name.trim(),
          phone: this.user?.phone?.trim(),
          login: usernameWithDomain.trim(),
          active: this.user.active,
          admin: this.user.admin,
          pages: this.adminUserPages(),
          profiles: this.generateProfiles(this.new_profiles),
          ecommerce: this.new_ecommerce,
        }
      }

      const payload = {
        name: this.user.name,
        email: alteredFields.login.trim(),
        phone: alteredFields.phone,
        active: alteredFields.active,
        permissionValue: {
          stores: this.stores,
          crm: {
            permissions: [...this.pages],
            roles: [...alteredFields.profiles],
            accessSensitiveData: this.user.accessSensitiveData,
            accessSensitiveDataEmployees: this.user.accessSensitiveDataEmployees,
            downloadSensitiveDataPermission: this.user.downloadSensitiveDataPermission,
          },
          ecommerce: {
            permissions: [...this.ecommercePermissions],
            roles: [...alteredFields.ecommerce],
          },
        },
      }

      payload.permissionValue.crm.permissions = this.generatePermissionsFromRoles(payload.permissionValue.crm.roles)
      payload.permissionValue.crm.accessSensitiveData = this.user.admin || this.roleHasPiiAccess(payload.permissionValue.crm.roles)

      if (this.user.admin) {
        const adminUserPages = ['usuarios/editar', 'usuarios/permissionamento', 'configuracoes/permissionamento-filiado']
        payload.permissionValue.crm.permissions.push(...adminUserPages)
        payload.permissionValue.crm.roles.push('admin')
        payload.permissionValue.crm.roles = payload.permissionValue.crm.roles.filter((value, index, array) => array.indexOf(value) === index)
        payload.permissionValue.ecommerce.roles = payload.permissionValue.ecommerce.roles.filter((value, index, array) => array.indexOf(value) === index)
        payload.permissionValue.crm.accessSensitiveDataEmployees = true
        payload.permissionValue.crm.downloadSensitiveDataPermission = true
      } else {
        payload.permissionValue.crm.accessSensitiveDataEmployees = false
      }

      if (this.user.password && this.user.password.length) {
        payload.password = this.user.password
      }

      if (this.user.newEmail && this.user.newEmail.length) {
        payload.newEmail = this.user.newEmail.trim()
      }

      if (this.user.crmAdmin) {
        payload.permissionValue.crm.roles.push(ROLE_ADMIN)
      }

      if (this.user.ecommerceAdmin) {
        payload.permissionValue.ecommerce.roles.push(ROLE_ADMIN)
      }

      return payload
    },
    async submitEdit() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        return
      }

      this.loading = true

      try {
        await this.$apollo.mutate({
          mutation: MUTATION_UPDATE_USER,
          variables: this.formatsPayloadToEdit(),
        })

        this.$emit('get-data')
        this.$emit('input', false)
        this.$snackbar({ message: 'Usuário salvo com sucesso', snackbarColor: '#4CAF50' })
        this.showNewEmailInput = false
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Não foi possível alterar este usuário', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
    async submitCreate() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        return
      }

      this.loading = true

      try {
        await this.$apollo.mutate({
          mutation: MUTATION_CREATE_USER,
          variables: this.formatsPayloadToCreate(),
        })

        this.$emit('get-data')
        this.$emit('input', false)
        this.$snackbar({ message: 'Usuário salvo com sucesso', snackbarColor: '#4CAF50' })
      } catch (error) {
        this.$snackbar({ message: 'Não foi possível criar este usuário' })
      } finally {
        this.loading = false
      }
    },
    adminUserPages() {
      let pages = this.user.pages
      let adminUserPages = ['usuarios/editar', 'usuarios/permissionamento']
      let arrayOfPages = []
      let arrayfilter = []
      const admin = this.user.admin

      if (admin && pages) {
        arrayOfPages = pages.filter((item) => item !== 'usuarios/editar' && item !== 'usuarios/permissionamento')
        arrayfilter = [...arrayOfPages, ...adminUserPages]
      } else if (pages) {
        arrayfilter = pages.filter((item) => item !== 'usuarios/editar' && item !== 'usuarios/permissionamento')
      }
      return arrayfilter
    },
    generatePermissionsFromRoles(roles) {
      let permissions = []
      roles
        .map((el) => this.profilesPages.find((ej) => ej.value === el)?.pages)
        .filter((el) => el)
        .forEach((el) => {
          permissions.push(...el)
          permissions = permissions.filter((value, index, array) => array.indexOf(value) === index)
        })
      return permissions
    },
    roleHasPiiAccess(roles) {
      const pii_access = roles.map((el) => this.profilesPages.find((ej) => ej.value === el)?.has_pii_access).filter((el) => el)
      return pii_access.length > 0
    },
  },
  validations() {
    const phone =
      this.user.phone || this.phoneRequired
        ? {
            phone: {
              required,
              minLength: minLength(18),
              maxLength: maxLength(19),
            },
          }
        : {}

    const defaultValidation = {
      name: {
        required,
      },
      ...phone,
      email: {
        required,
      },
      profiles: {
        required,
      },
      ecommerce: {
        required,
      },
    }

    if (this.user.profiles.length && !this.user.ecommerce.length) {
      defaultValidation.profiles = { required }
      delete defaultValidation.ecommerce
    }

    if (!this.user.profiles.length && this.user.ecommerce.length) {
      defaultValidation.ecommerce = { required }
      delete defaultValidation.profiles
    }

    if (this.isCreateUser) {
      return {
        user: {
          ...defaultValidation,
          password: {
            required,
            minLength: minLength(10),
            maxLength: maxLength(24),
            strongPassword,
          },
          passwordCheck: {
            required,
            minLength: minLength(10),
            maxLength: maxLength(24),
            strongPassword,
            samePassword: sameAs('password'),
          },
        },
      }
    } else if (this.user.profiles && this.user.password && this.user.password.length) {
      if (this.user.newEmail && this.user.newEmail.length) {
        return {
          user: {
            ...defaultValidation,
            newEmail: { required, notOnlySpace },
            password: {
              required,
              minLength: minLength(10),
              maxLength: maxLength(24),
              strongPassword,
            },
            passwordCheck: {
              required,
              minLength: minLength(10),
              maxLength: maxLength(24),
              strongPassword,
              samePassword: sameAs('password'),
            },
          },
        }
      }

      return {
        user: {
          ...defaultValidation,
          password: {
            required,
            minLength: minLength(10),
            maxLength: maxLength(24),
            strongPassword,
          },
          passwordCheck: {
            required,
            minLength: minLength(10),
            maxLength: maxLength(24),
            strongPassword,
            samePassword: sameAs('password'),
          },
        },
      }
    } else if (this.user.profiles) {
      if (this.user.newEmail && this.user.newEmail.length) {
        return {
          user: { ...defaultValidation, newEmail: { required, notOnlySpace } },
        }
      }
      return {
        user: defaultValidation,
      }
    } else {
      const { profiles, ...newValidation } = defaultValidation

      return {
        user: newValidation,
        new_profiles: {
          required,
        },
        new_ecommerce: {
          required,
        },
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.user-form-description {
  color: $cinza-0;
  font-size: 16px;
  line-height: 120%;
  word-break: normal;
}

.field-label {
  font-size: 16px;
  font-weight: 700;
  color: $cinza-0;
}
.field-text {
  font-size: 14px;
  color: #6b7580;
}
.help-icon {
  margin-left: 4px;
}
</style>
